/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useReportWebVitals } from "next/web-vitals";
import { useEffect } from "react";

const ENDPOINT = `${process.env.NEXT_PUBLIC_COSMOS_SITE_BASE_PATH ?? ""}/api/cosmos/metrics`;
const FLUSH_DELAY_MS = 5_000;

interface BufferedMetric {
	name: string;
	value: number;
	route: string;
}

const buffer: BufferedMetric[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function flush() {
	clearTimeout(timer);
	timer = undefined;
	const entries = buffer.splice(0);
	if (entries.length === 0) return;
	const payload = new Blob([JSON.stringify(entries)], { type: "application/json" });
	try {
		if (typeof navigator.sendBeacon === "function") {
			navigator.sendBeacon(ENDPOINT, payload);
		} else {
			fetch(ENDPOINT, { method: "POST", body: payload, keepalive: true }).catch(() => {});
		}
	} catch {
		// Best-effort; never block the UI
	}
}

function scheduleFlush() {
	if (!timer) {
		timer = setTimeout(flush, FLUSH_DELAY_MS);
	}
}

export function MetricsReporter() {
	useEffect(() => {
		const onVisChange = () => {
			if (document.visibilityState === "hidden") flush();
		};
		document.addEventListener("visibilitychange", onVisChange);
		return () => {
			document.removeEventListener("visibilitychange", onVisChange);
			flush();
		};
	}, []);

	useReportWebVitals((metric) => {
		buffer.push({ name: metric.name, value: metric.value, route: window.location.pathname });
		scheduleFlush();
	});

	return null;
}
